import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import confetti from "canvas-confetti";
import Lenis from "lenis";
import { 
  Heart, 
  Play, 
  Pause, 
  ChevronDown, 
  Sparkles, 
  RotateCcw,
  MessageCircle,
  Volume2,
  VolumeX,
  Camera
} from "lucide-react";
import { mockData } from "./mock";

export default function App() {
  const [started, setStarted] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [noButtonCount, setNoButtonCount] = useState(0);
  const [noButtonPos, setNoButtonPos] = useState({ x: 0, y: 0 });
  const [isYesPressed, setIsYesPressed] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [chapter2Muted, setChapter2Muted] = useState(true);
  const [chapter2Video2Muted, setChapter2Video2Muted] = useState(true);
  const [firstVideoEnded, setFirstVideoEnded] = useState(false);

  // Instagram feed (Simpleembed) — auto-pulls @thehahahawedding posts once the feed is connected
  const [igPosts, setIgPosts] = useState(null);
  useEffect(() => {
    fetch("https://simpleembed.com/api/feeds/udQf7k7jzQEDADe6S8pn")
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.posts) && data.posts.length > 0) {
          setIgPosts(data.posts);
        }
      })
      .catch(() => {});
  }, []);

  // Older posts beyond Simpleembed's free-tier cap, pinned manually
  const pinnedIgPosts = [
    { id: "DdbVPTnJj3Y", mediaType: "VIDEO", permalink: "https://www.instagram.com/reel/DdbVPTnJj3Y/", imageUrl: "/api/ig-thumbs/ig_DdbVPTnJj3Y.jpg" },
    { id: "DdbUa_zpCu1", mediaType: "IMAGE", permalink: "https://www.instagram.com/p/DdbUa_zpCu1/", imageUrl: "/api/ig-thumbs/ig_DdbUa_zpCu1.jpg" },
    { id: "DdZBS8JCVLg", mediaType: "IMAGE", permalink: "https://www.instagram.com/p/DdZBS8JCVLg/", imageUrl: "/api/ig-thumbs/ig_DdZBS8JCVLg.jpg" },
    { id: "DdY__FRJ_gB", mediaType: "VIDEO", permalink: "https://www.instagram.com/reel/DdY__FRJ_gB/", imageUrl: "/api/ig-thumbs/ig_DdY__FRJ_gB.jpg" },
    { id: "DdTnfsFiZhe", mediaType: "IMAGE", permalink: "https://www.instagram.com/p/DdTnfsFiZhe/", imageUrl: "/api/ig-thumbs/ig_DdTnfsFiZhe.jpg" },
  ];

  // Hero parallax
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImageY = useTransform(heroProgress, [0, 1], [0, 90]);
  const heroTextY = useTransform(heroProgress, [0, 1], [0, 140]);

  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const videoRef = useRef(null);
  const chapter2VideoRef = useRef(null);
  const chapter2Video2Ref = useRef(null);
  const firstVideoEndedRef = useRef(false);

  const noButtonPhrases = [
    "No 🙈",
    "Are you sure? 😳",
    "Harshita, seriously? 😂",
    "Think about the food…",
    "And my excellent jokes?",
    "Okay, I’ll ask nicely 🥺"
  ];

  // Countdown calculation
  useEffect(() => {
    const targetDate = new Date("2026-12-12T00:00:00").getTime();
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Lenis smooth momentum scrolling
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // Autoplay Chapter 2 videos ONLY when mostly in view; pause otherwise.
  // First video (top) plays with sound. Second video (below text) stays muted
  // until the first video has ended, then plays with sound.
  useEffect(() => {
    const v1 = chapter2VideoRef.current;
    const v2 = chapter2Video2Ref.current;

    const tryPlayWithSound = async (el, setMutedState) => {
      el.muted = false;
      el.volume = 1.0;
      try {
        await el.play();
        setMutedState(false);
      } catch (err) {
        el.muted = true;
        setMutedState(true);
        try { await el.play(); } catch (_) {}
      }
    };
    const playMuted = async (el, setMutedState) => {
      el.muted = true;
      setMutedState(true);
      try { await el.play(); } catch (_) {}
    };

    const observer1 = v1 ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            tryPlayWithSound(v1, setChapter2Muted);
          } else {
            v1.pause();
          }
        });
      },
      { threshold: [0, 0.15, 0.35, 0.6, 0.9] }
    ) : null;

    const observer2 = v2 ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            if (firstVideoEndedRef.current) {
              tryPlayWithSound(v2, setChapter2Video2Muted);
            } else {
              playMuted(v2, setChapter2Video2Muted);
            }
          } else {
            v2.pause();
          }
        });
      },
      { threshold: [0, 0.15, 0.35, 0.6, 0.9] }
    ) : null;

    if (v1 && observer1) observer1.observe(v1);
    if (v2 && observer2) observer2.observe(v2);

    return () => {
      if (observer1) observer1.disconnect();
      if (observer2) observer2.disconnect();
    };
  }, [started]);

  // When the first Chapter 2 video ends, unmute the second video if it's playing.
  const handleFirstVideoEnded = () => {
    firstVideoEndedRef.current = true;
    setFirstVideoEnded(true);
    const v2 = chapter2Video2Ref.current;
    if (v2 && !v2.paused) {
      v2.muted = false;
      v2.volume = 1.0;
      setChapter2Video2Muted(false);
      const p = v2.play();
      if (p && p.catch) p.catch(() => {});
    }
  };

  const handleStart = () => {
    setStarted(true);
  };

  const handleVideoPlayPause = () => {
    if (!videoRef.current) return;
    if (isVideoPlaying) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    } else {
      videoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch(err => console.log("Video play error:", err));
    }
  };

  const handleNoInteraction = () => {
    if (noButtonCount < 5) {
      setNoButtonCount(prev => prev + 1);
      const randomX = (Math.random() - 0.5) * 220;
      const randomY = (Math.random() - 0.5) * 120;
      setNoButtonPos({ x: randomX, y: randomY });
    }
  };

  const handleYesClick = () => {
    setIsYesPressed(true);
    try {
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#C8922E', '#D98FA3', '#7E2336', '#66705A', '#FBF8F1']
      });
      setTimeout(() => {
        confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 } });
        confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 } });
      }, 400);
    } catch (e) {
      console.log("Confetti error:", e);
    }
  };

  const handleWhatsAppSend = () => {
    const text = encodeURIComponent("Yes Aman! A million times YES! ♥ Forever started on 12.12.2026. - Harshita");
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="paper-texture min-h-screen text-[#2B2A28] relative selection:bg-[#D98FA3]/30">

      {/* 1. OPENING GATE */}
      <AnimatePresence>
        {!started && (
          <motion.div 
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.8 } }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#FBF8F1] px-6 text-center"
            data-testid="opening-gate-screen"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1 }}
              className="max-w-md w-full flex flex-col items-center"
            >
              {/* Attached H&A Logo */}
              <div className="relative mb-8 w-48 h-48 rounded-full flex items-center justify-center border-2 border-[#C8922E]/40 bg-white/80 shadow-2xl overflow-hidden p-2">
                <img 
                  src="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/mu07a6dd_WhatsApp%20Image%202026-07-31%20at%2012.46.17.jpeg" 
                  alt="H&A Logo" 
                  className="w-full h-full object-contain rounded-full"
                />
              </div>

              <motion.h1 
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="font-serif text-2xl md:text-3xl text-[#2B2A28] mb-8 font-medium italic"
              >
                Harshita, I made you something…
              </motion.h1>

              <motion.button
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ delay: 0.6 }}
                onClick={handleStart}
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#7E2336] text-[#FBF8F1] rounded-full shadow-lg hover:bg-[#681c2d] transition-all duration-300 font-medium tracking-wide text-lg border border-[#C8922E]/40"
                data-testid="tap-to-begin-btn"
              >
                <span>Tap to begin our story</span>
                <Heart className="w-5 h-5 text-[#D98FA3] fill-[#D98FA3] group-hover:scale-125 transition-transform" />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      {started && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="w-full flex flex-col items-center"
        >
          {/* 2. HERO SECTION */}
          <section ref={heroRef} className="relative w-full min-h-screen flex flex-col items-center justify-center text-center px-6 py-16 md:py-20 overflow-hidden bg-[#FBF8F1]">
            <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
              <div className="absolute top-1/4 left-1/6 w-2 h-2 rounded-full bg-[#C8922E]/60 animate-bounce"></div>
              <div className="absolute top-1/3 right-1/5 w-3 h-3 rounded-full bg-[#D98FA3]/50 animate-ping"></div>
            </div>

            <div className="relative z-20 max-w-3xl mx-auto flex flex-col items-center text-[#2B2A28]">
              <motion.h1
                style={{ y: heroTextY }}
                className="font-handwriting text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.15] text-[#7E2336] mb-8"
                data-testid="hero-main-title"
              >
                {["A tiny corner of the", "internet made", "especially for you"].map((line, li) => (
                  <span className="masked-line" key={li}>
                    <motion.span
                      initial={{ y: "110%", opacity: 0 }}
                      animate={{ y: "0%", opacity: 1 }}
                      transition={{ duration: 0.9, delay: 0.25 + li * 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1 }}
                className="font-serif italic text-lg md:text-2xl text-[#2B2A28]/80 mb-8"
                data-testid="hero-tap-hint"
              >
                Just tap when you’re ready. <span className="not-italic">🤍</span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.1, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
                style={{ y: heroImageY }}
                className="w-full max-w-lg mb-8"
              >
                <img
                  src="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/u5v9nm5y_HARSHITA%20SARAF%20BIO%20DATA-5.webp"
                  alt="Harshita — a little collage of her"
                  className="w-full h-auto rounded-xl shadow-2xl border border-[#C8922E]/30"
                  data-testid="hero-collage-image"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, y: [0, 8, 0] }}
                transition={{ opacity: { delay: 1.6 }, y: { repeat: Infinity, duration: 1.8 } }}
                className="flex flex-col items-center gap-2 cursor-pointer text-[#66705A] hover:text-[#7E2336]"
                onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                data-testid="scroll-indicator"
              >
                <ChevronDown className="w-6 h-6 text-[#C8922E]" />
              </motion.div>
            </div>
          </section>

          {/* Slow editorial marquee */}
          <div className="marquee w-full border-y border-[#C8922E]/25 bg-[#F5EEDC]/50 py-4" aria-hidden="true" data-testid="editorial-marquee">
            <div className="marquee-track font-serif text-sm md:text-base tracking-[0.35em] uppercase text-[#7E2336]/80">
              {[0, 1].map((dup) => (
                <span key={dup} className="inline-flex items-center gap-10">
                  <span>Aman</span><span className="text-[#D98FA3]">♥</span>
                  <span>Harshita</span><span className="text-[#C8922E]">✦</span>
                  <span>The HAHAHA Wedding</span><span className="text-[#C8922E]">✦</span>
                  <span>12 · 12 · 2026</span><span className="text-[#D98FA3]">♥</span>
                  <span>Forever begins here</span><span className="text-[#C8922E]">✦</span>
                </span>
              ))}
            </div>
          </div>

          {/* 3. OUR STORY SECTION */}
          <div className="w-full max-w-5xl mx-auto px-6 py-20 flex flex-col gap-28">
            
            {/* Chapter 1 — The Beginning */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center"
            >
              <div className="flex flex-col gap-4">
                <span className="font-handwriting text-2xl text-[#C8922E]">Chapter 1 — The Beginning</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#7E2336]">
                  “Two people who knew us best — our Sisters.”
                </h2>
                <p className="text-base md:text-lg text-[#2B2A28]/80 leading-relaxed">
                  They brought our worlds together, probably knowing something before we did. <strong className="font-semibold text-[#2B2A28]">Sisters by birth, Our Second Moms and Matchmakers by instinct.</strong> <span className="text-[#D98FA3]">💌</span>
                </p>
                <p className="font-handwriting text-xl md:text-2xl text-[#C8922E] leading-relaxed">
                  Thanks to Mamaji too &lt;3
                </p>
              </div>
              <div className="flex justify-center">
                <div className="polaroid transform rotate-2 max-w-sm w-full">
                  <img 
                    src="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/uhjwyjin_Two%20Sisters%20Chap%201.webp" 
                    alt="Our Two Sisters — the matchmakers" 
                    className="w-full h-auto object-contain rounded-sm mb-4 shadow-sm bg-[#FBF8F1]"
                  />
                  <p className="font-handwriting text-center text-xl text-[#66705A]">
                    Where it all started ✨
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Chapter 2 — Let's Meet */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center gap-10"
            >
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10 items-start justify-items-center">
                <div className="polaroid transform -rotate-3 max-w-sm w-full">
                  <div className="relative w-full h-80 rounded-sm mb-4 overflow-hidden bg-[#2B2A28]">
                    <video
                      ref={chapter2VideoRef}
                      className="w-full h-full object-cover"
                      playsInline
                      preload="auto"
                      onEnded={handleFirstVideoEnded}
                      poster="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/uyq3ozy2_8Y3A6055.webp"
                      data-testid="chapter2-autoplay-video"
                    >
                      <source
                        src="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/hbu2xx2t_Untitled%20design.mp4"
                        type="video/mp4"
                      />
                    </video>
                    <button
                      type="button"
                      onClick={() => {
                        const v = chapter2VideoRef.current;
                        if (!v) return;
                        v.muted = !v.muted;
                        setChapter2Muted(v.muted);
                        if (v.paused) v.play().catch(() => {});
                      }}
                      className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm border border-white/20 transition-colors"
                      aria-label={chapter2Muted ? "Unmute video" : "Mute video"}
                      data-testid="chapter2-video-sound-toggle"
                    >
                      {chapter2Muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="font-handwriting text-center text-xl text-[#66705A]">
                    The Most Important Flight
                  </p>
                </div>

                <div className="polaroid transform rotate-2 max-w-sm w-full">
                  <div className="w-full h-80 rounded-sm mb-4 overflow-hidden bg-[#FBF8F1] flex items-center justify-center">
                    <img 
                      src="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/kawv953s_IMG_7577.webp" 
                      alt="Our first WhatsApp conversation" 
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <p className="font-handwriting text-center text-xl text-[#66705A]">
                    The very first hello ✨
                  </p>
                </div>
              </div>

              <div className="w-full max-w-3xl flex flex-col gap-4 text-center md:text-left items-center md:items-start">
                <span className="font-handwriting text-2xl text-[#C8922E]">Chapter 2 — Let’s Meet</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#7E2336]">
                  “Every table brought us closer.”
                </h2>
                <p className="text-base md:text-lg text-[#2B2A28]/80 leading-relaxed">
                  From The Bombay Westin, Mirai and Pardon Our French to… Misu, defeating you at Loco Lane Games and JW Golfshire in Bangalore. Every meeting brought us closer, until everything became clear at <strong className="font-semibold text-[#2B2A28]">The Brown Table</strong>.
                </p>
              </div>

              <div className="w-full flex justify-center mt-4">
                <div className="polaroid transform rotate-1 max-w-lg w-full">
                  <div className="relative w-full rounded-sm mb-4 overflow-hidden bg-[#2B2A28]">
                    <video
                      ref={chapter2Video2Ref}
                      className="w-full h-auto block"
                      playsInline
                      preload="auto"
                      data-testid="chapter2-autoplay-video-2"
                    >
                      <source
                        src="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/6uqcocb5_VIDEO-2026-03-12-16-22-04.mov"
                        type="video/mp4"
                      />
                    </video>
                    <button
                      type="button"
                      onClick={() => {
                        const v = chapter2Video2Ref.current;
                        if (!v) return;
                        v.muted = !v.muted;
                        setChapter2Video2Muted(v.muted);
                        if (v.paused) v.play().catch(() => {});
                      }}
                      className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm border border-white/20 transition-colors"
                      aria-label={chapter2Video2Muted ? "Unmute video" : "Mute video"}
                      data-testid="chapter2-video-sound-toggle-2"
                    >
                      {chapter2Video2Muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="font-handwriting text-center text-xl text-[#66705A]">
                    What Aanu &amp; I Loved About You From Day One
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Chapter 3 — Why You (Scrapbook Cards) */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center text-center bg-white/70 backdrop-blur-sm p-8 md:p-12 rounded-2xl shadow-sm border border-[#C8922E]/20 relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 text-[#C8922E]/20">
                <Heart className="w-24 h-24 fill-[#C8922E]/10" />
              </div>
              
              <span className="font-handwriting text-2xl text-[#C8922E] mb-2">Chapter 3 — The Handwritten Notes</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#7E2336] mb-8">
                From Me To You
              </h2>

              <div className="max-w-xl w-full min-h-[160px] flex items-center justify-center p-6 bg-[#FFFDF9] rounded-xl shadow-md border border-[#C8922E]/30 mb-8 relative">
                <p className="font-handwriting text-2xl md:text-3xl text-[#7E2336]">
                  “{mockData.storyChapters[2].cards[activeCardIndex]}”
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-2 max-w-xl">
                {mockData.storyChapters[2].cards.map((card, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveCardIndex(idx)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      activeCardIndex === idx 
                        ? "bg-[#7E2336] text-[#FBF8F1] shadow-sm scale-105" 
                        : "bg-[#FFFDF9] text-[#2B2A28] border border-[#C8922E]/30 hover:bg-[#C8922E]/10"
                    }`}
                    data-testid={`scrapbook-card-btn-${idx}`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Chapter 4 — Our Families */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col gap-8"
            >
              <div className="max-w-2xl">
                <span className="font-handwriting text-2xl text-[#C8922E]">Chapter 4 — Our Families</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#7E2336] mt-1 mb-3">
                  “Two people became one big, beautiful family.”
                </h2>
                <p className="text-base md:text-lg text-[#2B2A28]/80 leading-relaxed">
                  Your love didn’t just bring two hearts together. It brought families, laughter, blessings and a whole new meaning to home.
                </p>
              </div>

              <div className="columns-1 sm:columns-2 md:columns-3 gap-6 [column-fill:_balance]">
                {mockData.storyChapters[3].familyImages.map((imgUrl, fIdx) => (
                  <div key={fIdx} className={`polaroid mb-6 break-inside-avoid transform ${fIdx % 2 === 0 ? 'rotate-1' : '-rotate-1'} hover:rotate-0`}>
                    <img 
                      src={imgUrl} 
                      alt={`Family moment ${fIdx + 1}`} 
                      className="w-full h-auto object-contain rounded-sm bg-[#FBF8F1]"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Chapter 5 — The Roka */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center"
            >
              <div className="order-2 md:order-1 flex justify-center">
                <div className="polaroid transform rotate-2 max-w-sm w-full">
                  <img 
                    src="https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/bwjzzd6f_AMAN7098.webp" 
                    alt="The Roka Celebration" 
                    className="w-full h-auto object-contain rounded-sm mb-4 bg-[#FBF8F1]"
                  />
                  <p className="font-handwriting text-center text-xl text-[#66705A]">
                    March 28 — Forever began
                  </p>
                </div>
              </div>
              <div className="order-1 md:order-2 flex flex-col gap-4">
                <span className="font-handwriting text-2xl text-[#C8922E]">Chapter 5 — The Roka</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#7E2336]">
                  “A little piece of forever…”
                </h2>
                <p className="text-base md:text-lg text-[#2B2A28]/80 leading-relaxed">
                  March 28 gave our story another beautiful beginning. Surrounded by everyone we love, forever stopped feeling like a dream and started feeling like our future.
                </p>
              </div>
            </motion.div>

            {/* The HAHAHA Wedding Celebrations — Instagram */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center gap-8 w-full"
              data-testid="hahaha-celebrations-section"
            >
              <div className="text-center max-w-2xl">
                <span className="font-handwriting text-2xl text-[#C8922E]">The HAHAHA Wedding</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#7E2336] mt-1 mb-3">
                  The HAHAHA Wedding Celebrations
                </h2>
                <p className="text-base md:text-lg text-[#2B2A28]/80 leading-relaxed">
                  All our moments, one post at a time. Follow along on Instagram for every laugh, every dance and every happy tear.
                </p>
              </div>

              <a
                href="https://instagram.com/thehahahawedding?obrf=MWg0OHRyZmh0c3llag%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-7 py-3.5 bg-gradient-to-tr from-[#833AB4] via-[#E1306C] to-[#F77737] text-white rounded-full shadow-lg hover:shadow-xl hover:scale-[1.03] transition-all font-medium tracking-wide"
                data-testid="instagram-profile-link"
              >
                <Camera className="w-5 h-5" />
                <span>@thehahahawedding on Instagram</span>
              </a>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 w-full max-w-3xl">
                {(igPosts
                  ? [...igPosts, ...pinnedIgPosts.filter(
                      (p) => !igPosts.some((ip) => (ip.permalink || "").includes(p.id))
                    )].map((post, idx) => ({
                      key: post.id || idx,
                      href: post.permalink || "https://instagram.com/thehahahawedding?obrf=MWg0OHRyZmh0c3llag%3D%3D",
                      img: post.imageUrl,
                      alt: post.caption ? post.caption.slice(0, 80) : `HAHAHA Wedding post ${idx + 1}`,
                      type: post.mediaType,
                    }))
                  : [
                      "https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/lm2dwrnh_8Y3A6031.webp",
                      "https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/wc4d8hcp_8Y3A5955.webp",
                      "https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/9oah6f4g_AMAN7154.webp",
                      "https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/fv0qms43_8Y3A5996.webp",
                      "https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/owirjwuo_8Y3A6055.webp",
                      "https://customer-assets-lxgj4vgw.emergentagent.net/job_forever-starts-here-34/artifacts/bwjzzd6f_AMAN7098.webp"
                    ].map((imgUrl, idx) => ({
                      key: idx,
                      href: "https://instagram.com/thehahahawedding?obrf=MWg0OHRyZmh0c3llag%3D%3D",
                      img: imgUrl,
                      alt: `HAHAHA Wedding celebration ${idx + 1}`,
                      type: null,
                    }))
                ).map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl border border-[#C8922E]/30 aspect-square block"
                    data-testid={`celebration-post-${item.key}`}
                  >
                    <img
                      src={item.img}
                      alt={item.alt}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    {item.type === "VIDEO" && (
                      <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 backdrop-blur-sm">
                        <Play className="w-3.5 h-3.5 fill-white" />
                      </span>
                    )}
                    {item.type === "CAROUSEL_ALBUM" && (
                      <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 backdrop-blur-sm">
                        <Camera className="w-3.5 h-3.5" />
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-300 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </a>
                ))}
              </div>

              <p className="text-sm text-[#66705A] text-center max-w-md">
                Tap any photo to view the full post on Instagram.
              </p>
            </motion.div>

            {/* Chapter 6 — What I Know Now (Quote) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-[#FFFDF9] border border-[#C8922E]/30 p-10 md:p-16 rounded-2xl text-center shadow-lg relative overflow-hidden my-6"
            >
              <div className="absolute top-0 right-0 transform translate-x-4 -translate-y-4 opacity-10">
                <Heart className="w-40 h-40 fill-[#7E2336]" />
              </div>
              <p className="font-serif text-xl md:text-2xl italic text-[#7E2336] leading-relaxed mb-6">
                “When everything changes around us, one thing stays constant—the way our hearts find each other, always connected.”
              </p>
              <p className="font-handwriting text-2xl md:text-3xl text-[#C8922E]">
                “Harshita, all along there really was some invisible string tying me to you.”
              </p>
            </motion.div>

          </div>

          {/* 5. THE PROPOSAL SECTION */}
          <section className="w-full max-w-3xl mx-auto px-6 py-28 flex flex-col items-center text-center relative">
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-96 h-96 rounded-full bg-[#C8922E]/5 blur-3xl"></div>
            </div>

            <AnimatePresence mode="wait">
              {!isYesPressed ? (
                <motion.div
                  key="proposal-scene"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="w-full flex flex-col items-center z-10"
                >
                  <div className="mb-6 inline-flex p-3 rounded-full bg-[#C8922E]/10 border border-[#C8922E]/30 text-[#C8922E]">
                    <Heart className="w-8 h-8 fill-[#C8922E]/20" />
                  </div>

                  <h3 className="font-handwriting text-3xl md:text-4xl text-[#66705A] mb-3">
                    I’ve loved every chapter so far…
                  </h3>
                  <p className="font-serif text-xl md:text-2xl text-[#7E2336] mb-8 italic">
                    But my favourite one is the one we haven’t written yet.
                  </p>

                  <div className="bg-[#FFFDF9] border-2 border-[#C8922E]/40 p-8 md:p-12 rounded-2xl shadow-xl w-full mb-12 relative">
                    <h2 className="font-serif text-4xl md:text-6xl font-bold text-[#7E2336] mb-4" data-testid="main-proposal-question">
                      “Harshita, will you marry me?”
                    </h2>
                    <p className="font-handwriting text-2xl text-[#C8922E]">
                      Again. Properly. Dramatically. Forever.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-6 relative min-h-[90px] w-full">
                    <motion.button
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.95 }}
                      style={{ scale: 1 + noButtonCount * 0.08 }}
                      onClick={handleYesClick}
                      className="px-8 py-4 bg-[#7E2336] text-[#FBF8F1] rounded-full shadow-xl font-bold text-lg hover:bg-[#681c2d] transition-all border border-[#C8922E]/50 z-20 flex items-center gap-2"
                      data-testid="proposal-yes-btn"
                    >
                      <span>YES, obviously!</span>
                      <Heart className="w-5 h-5 fill-[#D98FA3] text-[#D98FA3]" />
                    </motion.button>

                    <motion.button
                      animate={noButtonCount > 0 && noButtonCount < 6 ? { x: noButtonPos.x, y: noButtonPos.y } : {}}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      onMouseEnter={handleNoInteraction}
                      onClick={handleNoInteraction}
                      className="px-6 py-3.5 bg-[#FFFDF9] text-[#2B2A28] rounded-full shadow-md font-medium text-base hover:bg-gray-100 transition-colors border border-gray-300 z-10"
                      data-testid="proposal-no-btn"
                    >
                      {noButtonPhrases[Math.min(noButtonCount, noButtonPhrases.length - 1)]}
                    </motion.button>
                  </div>

                  {noButtonCount > 0 && (
                    <motion.p 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      className="text-xs text-[#C8922E] font-medium mt-6"
                    >
                      {noButtonCount < 6 ? `Attempt ${noButtonCount}/6: Nice try! 😉` : "Okay okay, the No button has retired. You know your only real option here! 😂"}
                    </motion.p>
                  )}
                </motion.div>
              ) : (
                /* CELEBRATION SCREEN WITH COUNTDOWN TIMER */
                <motion.div
                  key="celebration-screen"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="w-full bg-[#FFFDF9] border-2 border-[#C8922E] p-10 md:p-16 rounded-2xl shadow-2xl flex flex-col items-center text-center z-10"
                  data-testid="celebration-screen"
                >
                  <div className="w-20 h-20 rounded-full bg-[#7E2336] text-[#FBF8F1] flex items-center justify-center mb-6 shadow-lg animate-bounce">
                    <Heart className="w-10 h-10 fill-[#D98FA3] text-[#D98FA3]" />
                  </div>

                  <span className="font-handwriting text-3xl text-[#C8922E] mb-2">SHE SAID YES!</span>
                  <h2 className="font-serif text-4xl md:text-6xl font-bold text-[#7E2336] mb-4">
                    Best Decision Ever.
                  </h2>
                  <p className="text-lg text-[#2B2A28]/80 mb-6 max-w-md">
                    See you at forever — <span className="font-bold text-[#7E2336]">12.12.2026</span>
                  </p>

                  {/* COUNTDOWN TIMER */}
                  <div 
                    className="grid grid-cols-4 gap-3 md:gap-4 my-6 bg-[#FBF8F1] border border-[#C8922E]/40 px-6 py-4 rounded-2xl shadow-md max-w-md w-full"
                    data-testid="wedding-countdown-timer"
                  >
                    <div className="flex flex-col items-center">
                      <span className="font-serif text-2xl md:text-3xl font-bold text-[#7E2336]">{timeLeft.days}</span>
                      <span className="text-[9px] uppercase tracking-widest text-[#66705A]">Days</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="font-serif text-2xl md:text-3xl font-bold text-[#7E2336]">{timeLeft.hours}</span>
                      <span className="text-[9px] uppercase tracking-widest text-[#66705A]">Hours</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="font-serif text-2xl md:text-3xl font-bold text-[#7E2336]">{timeLeft.minutes}</span>
                      <span className="text-[9px] uppercase tracking-widest text-[#66705A]">Mins</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="font-serif text-2xl md:text-3xl font-bold text-[#7E2336]">{timeLeft.seconds}</span>
                      <span className="text-[9px] uppercase tracking-widest text-[#66705A]">Secs</span>
                    </div>
                  </div>

                  <div className="p-6 bg-[#FBF8F1] rounded-xl border border-[#C8922E]/30 mb-8 w-full max-w-md">
                    <p className="font-serif text-2xl italic text-[#7E2336] mb-2">“Harshita & Aman”</p>
                    <p className="text-xs text-[#66705A]">The HAHAHA Wedding · 12.12.2026</p>
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-4">
                    <button
                      onClick={handleWhatsAppSend}
                      className="px-6 py-3 bg-[#25D366] text-white rounded-full shadow-md font-medium text-sm hover:bg-[#20ba5a] transition-all flex items-center gap-2"
                      data-testid="whatsapp-send-btn"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>Send Aman a ♥ (WhatsApp)</span>
                    </button>

                    <button
                      onClick={() => {
                        setIsYesPressed(false);
                        setNoButtonCount(0);
                      }}
                      className="px-6 py-3 bg-[#7E2336] text-[#FBF8F1] rounded-full shadow-md font-medium text-sm hover:bg-[#681c2d] transition-all flex items-center gap-2"
                      data-testid="replay-story-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Replay our story</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>

          {/* 6. FOOTER */}
          <footer className="w-full bg-[#F5EEDC]/80 border-t border-[#C8922E]/30 py-16 px-6 text-center">
            <div className="max-w-xl mx-auto flex flex-col items-center">
              <div className="w-14 h-14 rounded-full border border-[#C8922E] flex items-center justify-center bg-white mb-4 shadow-sm">
                <span className="font-serif text-xl font-bold text-[#7E2336]">H&A</span>
              </div>
              <p className="font-handwriting text-2xl text-[#7E2336] mb-2">
                “Handmade with love, questionable jokes and a lot of hope.”
              </p>
              <p className="text-xs md:text-sm text-[#66705A] tracking-wider uppercase">
                The HAHAHA Wedding · 12.12.2026 · thehahahawedding.com
              </p>
            </div>
          </footer>

        </motion.div>
      )}

    </div>
  );
}
